<template>
  <div>
    <label
      :id="text.toLowerCase().replaceAll(' ', '-')"
      class="twcs-block twcs-text-base twcs-font-medium twcs-text-kctcs-navy-800"
    >
      <template v-if="options.length > 0">
        {{ text }}
      </template>
      <template v-else>
        Loading...
      </template>
    </label>
    <div :class="['twcs-mt-1 twcs-relative']">
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        :aria-label="multiselect ? text : false"
        :aria-labelledby="!multiselect ? text.toLowerCase().replaceAll(' ', '-') : false"
        :disabled="options.length === 0 || loading"
        ref="button"
        @click="onButtonClick"
        :class="[loading ? 'twcs-bg-gray-300' : 'twcs-bg-white',
        'twcs-relative twcs-w-48 twcs-border twcs-border-gray-300 twcs-shadow-sm twcs-pl-3 twcs-pr-10 twcs-py-2 twcs-text-left twcs-cursor-default focus-visible:twcs-outline-none focus-visible:twcs-ring-1 focus-visible:twcs-ring-kctcs-navy-500 focus-visible:twcs-border-kctcs-navy-500 twcs-text-base']"
      >
        <span v-if="loading" class="twcs-block twcs-truncate">
          Loading...
        </span>
        <span v-else class="twcs-block twcs-truncate">
          {{ comboboxText }}
        </span>
        <span class="twcs-absolute twcs-inset-y-0 twcs-right-0 twcs-flex twcs-items-center twcs-pr-2 twcs-pointer-events-none">
          <!-- Heroicon name: selector -->
          <svg class="twcs-h-5 twcs-w-5 twcs-text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </span>
      </button>
      <Transition
        leave-active-class="twcs-transition twcs-ease-in twcs-duration-100"
        leave-from-class="twcs-opacity-100"
        leave-to-class="twcs-opacity-0"
      >
        <!--
          Select popover, show/hide based on select state.

          Entering: ""
            From: ""
            To: ""
          Leaving: "transition ease-in duration-100"
            From: "opacity-100"
            To: "opacity-0"
        -->
        <div
          v-show="isOpen"
          v-click-outside="onClickAway"
          class="twcs-absolute twcs-right-0 twcs-left-0 twcs-mt-1 twcs-w-min twcs-min-w-full twcs-bg-white twcs-shadow-lg twcs-z-[1000]"
        >
          <ul
            ref="listbox"
            @keydown.enter.stop.prevent="onOptionSelect()"
            @keydown.space.stop.prevent="onOptionSelect()"
            @keydown.arrow-up.prevent="onArrowUp()"
            @keydown.arrow-down.prevent="onArrowDown()"
            @keydown.escape="onEscape()"
            tabindex="0"
            role="listbox"
            :aria-labelledby="text.toLowerCase().replaceAll(' ', '-')"
            :aria-activedescendant="activeDescendant"
            :aria-multiselectable="multiselect"
            class="twcs-max-h-60 twcs-text-base twcs-ring-1 twcs-ring-black twcs-ring-opacity-5 twcs-overflow-y-auto twcs-w-full focus-visible:twcs-outline-none sm:twcs-text-base"
          >
            <!--
              Select option, manage highlight styles based on mouseenter/mouseleave and keyboard navigation.

              Highlighted: "text-white bg-kctcs-navy-600", Not Highlighted: "text-gray-900"
            -->
            <li
              v-for="(option, index) in options"
              :key="option.text"
              role="option"
              :id="`${source}-item-${index}`"
              :aria-selected="values[index]"
              @click="choose(index)"
              @mouseenter="selected = index"
              @mouseleave="selected = null"
              :class="[selected === index ? 'twcs-text-white twcs-bg-kctcs-navy-600' : 'twcs-text-gray-900',
              'twcs-cursor-default twcs-select-none twcs-relative twcs-w-full twcs-py-2 twcs-pl-8 twcs-pr-4']"
            >
              <!-- Selected: "font-semibold", Not Selected: "font-normal" -->
              <span
                :class="[values[index] ? 'twcs-font-semibold' : 'twcs-font-normal', 'twcs-block twcs-truncate twcs-w-full']"
              >
                {{ option.text }}
              </span>
              <!--
                Checkmark, only display for selected option.

                Highlighted: "text-white", Not Highlighted: "text-kctcs-navy-600"
              -->
              <span
                v-if="values[index]"
                aria-hidden="true"
                :class="[selected === index ? 'twcs-text-white' : 'twcs-text-kctcs-navy-600',
                'twcs-absolute twcs-inset-y-0 twcs-left-0 twcs-flex twcs-items-center twcs-pl-1.5']"
              >
                <!-- Heroicon name: check -->
                <svg class="twcs-h-5 twcs-w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </span>
            </li>
          </ul>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import Bugsnag from '@bugsnag/js';

let props = defineProps({
	// text to show/key in options object in parent component (when lowercased)
	text: {
		type: String,
		required: true,
	},
	// if lowercase of text is not the 'source', is set here
	source: {
		type: String,
		required: true,
	},
	// array of options
	options: {
		type: Array,
		default: () => [],
	},
	//
	loading: {
		type: Boolean,
		default: true,
	},
	// array of booleans that represent if the corresponding option
	// (at same index in options array) is selected
	values: {
		type: Array,
		default: () => [],
	},
	// determines if dropdown is multiselect
	multiselect: {
		type: Boolean,
		default: false,
	},
});

let emit = defineEmits(['filterChanged', 'filtersFinishedChanging']);

let isOpen = ref(false);
let selected = ref(0); // hover/focused element
let listbox = ref(null); // ul element
let button = ref(null); // button element
let activeDescendant = ref(''); // active active descendant of ul element
let isMounted = ref(false);

let comboboxText = computed(() => {
    if (props.multiselect) {
        let selectedOptionsText = props.options.filter((_, index) => props.values[index]).map((option) => option.text);
        if (selectedOptionsText.length === 1) {
            return selectedOptionsText[0];
        } else if (selectedOptionsText.length === 2) {
            return selectedOptionsText[0] + ' and ' + selectedOptionsText[1];
        } else {
            return selectedOptionsText.reduce((carry, current, index, array) => index === 0 ? current :  index === array.length - 1 ? carry + ', and ' + current : carry + ', ' + current, '');
        }
    } else {
        return props.options[props.values.indexOf(true)].text;
    }
});

onMounted(() => {
    isMounted.value = true;
});

function setActiveDescendant() {
    activeDescendant.value = `${props.source}-item-${selected.value}`;
}

function onClickAway(event, el) {
	isOpen.value = false;
}

function onButtonClick(event, el) {
	if(isOpen.value) {
		isOpen.value = false;
		return;
	}
	isOpen.value = true;
	nextTick(() => {
        selected.value = 0;
        setActiveDescendant();
		listbox.value.focus();
        if (isMounted.value) {
            listbox.value.children[selected.value].scrollIntoView({ block: 'nearest' });
        }
	});
}

function choose(index) {
    try {
        if(!props.multiselect) { // case: not multiselect
            // if one if selected all others must be false
            props.values.forEach((value, idx, values) => {
                if(values[idx]) {
                    emit('filterChanged', { source: props.source, text: props.text, index: idx, value: false });
                }
            });
            emit('filterChanged', { source: props.source, text: props.text, index, value: true });
            nextTick(() => {
                isOpen.value = false;
            });
        } else { // case: is multiselect
            if(props.values[index] && !(props.options[index].hasOwnProperty('type') && props.options[index].type === '*')) {
                // if the selected option is already selected and not the catch-all, deselect it
                emit('filterChanged', { source: props.source, text: props.text, index, value: false });
                if(!props.values.includes(true)) {
                    // if after deselection all options are unselected, find catch-all and select it if it exists
                    props.options.forEach((option, idx) => {
                        if(option.hasOwnProperty('type') && option.type === '*') {
                            emit('filterChanged', { source: props.source, text: props.text, index: idx, value: true });
                        }
                    });
                }
            } else if(props.options[index].hasOwnProperty('type') && props.options[index].type === '*') {
                // if option is catch-all
                if(!props.values[index]) {
                    // if catch-all is not already selected, deselect all options and select catch-all
                    props.values.forEach((value, idx, values) => {
                        if(values[idx]) {
                            emit('filterChanged', { source: props.source, text: props.text, index: idx, value: false });
                        }
                    });
                    emit('filterChanged', { source: props.source, text: props.text, index, value: true });
                }
            } else {
                // else
                emit('filterChanged', { source: props.source, text: props.text, index, value: true });
                props.options.forEach((option, idx) => {
                    if(option.hasOwnProperty('type') && option.type === '*' && props.values[idx]) {
                        emit('filterChanged', { source: props.source, text: props.text, index: idx, value: false });
                    }
                });
            }
        }
    } catch (error) {
        Bugsnag.notify(error, (event) => {
            event.addMetadata('dropdown', {
                source: props.source,
                text: props.text,
                options: props.options,
                values: props.values,
                multiselect: props.multiselect,
                index: index,
            });
        });
    }
	emit('filtersFinishedChanging');
}

function onOptionSelect() {
	choose(selected.value);
	if(!props.multiselect) {
		button.value.focus();
	}
}

function onArrowUp() {
	selected.value = selected.value - 1 < 0 ? props.options.length - 1 : selected.value - 1;
    setActiveDescendant();
    if (isMounted.value) {
	    listbox.value.children[selected.value].scrollIntoView({ block: 'nearest' });
    }
}

function onArrowDown() {
	selected.value = selected.value + 1 > props.options.length - 1 ? 0 : selected.value + 1;
    setActiveDescendant();
    if (isMounted.value) {
	    listbox.value.children[selected.value].scrollIntoView({ block: 'nearest' });
    }
}

function onEscape() {
	isOpen.value = false;
    nextTick(() => {
        button.value.focus();
    });
}
</script>
